<template>
  <v-container>
    <v-form ref="form" v-model="isFormValid" lazy-validation>
      <v-row>
        <v-col cols="12">
          <v-card class="px-3 mt-4 pt-7">
            <v-row
              class="px-4"
            >
              <v-col cols="12">
                <SimplePageHeader
                  v-if="!isEdit"
                  title="Add Contract"
                />
                <SimplePageHeader
                  v-else
                  title="Edit Contract"
                >
                  <template v-slot:right-buttons>
                    <v-switch
                      v-model="active"
                      label="Active"
                      class="float-end"
                      :readonly="isReadOnlyAccess"
                    ></v-switch>
                  </template>
                </SimplePageHeader>
              </v-col>
            </v-row>
            <v-card-text>
              <v-tabs v-model="tab">
                <v-tab :key="BASIC_INFORMATION">
                  Basic Information
                </v-tab>
                <v-tab :key="CONTRACT_DETAILS">
                  Contract Details
                </v-tab>
                <v-tab :key="COMMISSIONS">
                  Commissions
                </v-tab>
                <v-tab :key="EXPENSES">
                  Expenses
                </v-tab>
              </v-tabs>
              <v-tabs-items v-model="tab">
                <v-tab-item :key="BASIC_INFORMATION">
                  <v-row class="my-5">
                    <v-col cols="12" sm="6">
                      <UsersDropDownList
                        :userId="userId"
                        :isRequired="true"
                        label="Consultant"
                        :readonly="isReadOnlyAccess"
                        :onlyActiveUsers="!isEdit ? true : null"
                        @userIdChange="handleUserIdChange"
                      ></UsersDropDownList>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <ClientsDropDownList
                        :is-required="true"
                        :id="clientId"
                        :readonly="isReadOnlyAccess"
                        @clientIdChange="handleClientIdChange">
                      </ClientsDropDownList>
                    </v-col>
                  </v-row>
                </v-tab-item>
                <v-tab-item :key="CONTRACT_DETAILS">
                  <v-row class="mt-3">
                    <v-col cols="12" md="6">
                      <v-row>
                        <v-col cols="12">
                          <v-card-title class="px-0 py-0">Contract</v-card-title>
                        </v-col>
                      </v-row>
                      <v-row class="mt-0">
                        <v-col cols="12" sm="6">
                          <v-text-field
                            label="Job Title"
                            v-model="jobTitle"
                            :readonly="isReadOnlyAccess"
                            outlined
                            dense
                            :rules="[Required('Job Title')]"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="6">
                          <FormattedNumberField
                            label="Hours Worked Monthly"
                            :value="workedHoursAverage"
                            :required="true"
                            :readonly="isReadOnlyAccess"
                            :dense="true"
                            @valueChange="(newValue) => this.workedHoursAverage = newValue"
                          ></FormattedNumberField>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="12" sm="6">
                          <PayFrequenciesDropdownList
                            :isRequired="true"
                            :payFrequency="payFrequencyId"
                            :readonly="isReadOnlyAccess"
                            :dense="true"
                            @payFrequencyChangeHandler="(newValue) => this.payFrequencyId = newValue">
                          </PayFrequenciesDropdownList>
                        </v-col>
                        <v-col cols="12" sm="6">
                          <ContractTypesDropDownList
                            :id="contractTypeId"
                            :isRequired="true"
                            :readonly="isReadOnlyAccess"
                            :dense="true"
                            @change="(newValue) => {
                              this.contractTypeId = newValue
                              this.lengthOfContract = null
                              this.conversionFee = null
                            }"
                          ></ContractTypesDropDownList>
                        </v-col>
                      </v-row>
                      <v-row v-if="isContractToHire">
                        <v-col cols="12" sm="6">
                          <FormattedNumberField
                            label="Length of Contract (Months)"
                            :value="lengthOfContract"
                            :readonly="isReadOnlyAccess"
                            :rules="[isLengthOfContractRequired]"
                            :dense="true"
                            :onlyInt="true"
                            :validateOnBlur="true"
                            @valueChange="(newValue) => this.lengthOfContract = newValue"
                          ></FormattedNumberField>
                        </v-col>
                        <v-col cols="12" sm="6">
                          <FormattedNumberField
                            label="Conversion Fee"
                            :value="conversionFee"
                            :readonly="isReadOnlyAccess"
                            :rules="[isConversionFeeRequired]"
                            :dense="true"
                            :validateOnBlur="true"
                            prepend-inner-icon="attach_money"
                            @valueChange="(newValue) => this.conversionFee = newValue"
                          ></FormattedNumberField>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="12" sm="6">
                          <SimpleDatePicker
                            label="Start Date"
                            :value="startAt"
                            :isRequired="true"
                            :readonly="isReadOnlyAccess"
                            :dense="true"
                            @selected-date-change="(newValue) => this.startAt = newValue"
                          ></SimpleDatePicker>
                        </v-col>
                        <v-col cols="12" sm="6">
                          <SimpleDatePicker
                            label="End Date"
                            :value="endAt"
                            :isRequired="isContractToHire || !active"
                            :clearable="!isReadOnlyAccess"
                            :readonly="isReadOnlyAccess"
                            :dense="true"
                            :min-date="startAt"
                            :min-date-enable="true"
                            @selected-date-change="endDateChangeHandler"
                          ></SimpleDatePicker>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="12" sm="6">
                          <v-radio-group
                            label="Consultant Type"
                            class="mt-0"
                            v-model="employeeTypeId"
                            :rules="[isFilledOut()]"
                            :readonly="isReadOnlyAccess"
                            dense
                            @change="() => {this.corporateName = null; this.ein = null;}"
                          >
                            <v-radio class="my-0" label="1099" :value="INDEPENDENT_CONTRACTOR"></v-radio>
                            <v-radio class="my-0" label="C2C" :value="CORP_TO_CORP"></v-radio>
                            <v-radio class="my-0" label="W2 no Benefits" :value="EMPLOYEE_NO_BENEFITS"></v-radio>
                            <v-radio class="my-0" label="W2 + Benefits" :value="EMPLOYEE_WITH_BENEFITS"></v-radio>
                          </v-radio-group>
                        </v-col>
                        <v-col cols="12" sm="6" v-if="isC2C">
                          <v-row>
                            <v-col cols="12" class="pt-5">
                              <v-text-field
                                label="Corp. Name"
                                v-model="corporateName"
                                validate-on-blur
                                :rules="[isCorporateNameRequired]"
                                outlined
                                dense
                              ></v-text-field>
                            </v-col>
                            <v-col cols="12" class="py-0">
                              <v-text-field
                                label="EIN"
                                v-model="ein"
                                validate-on-blur
                                :rules="[isEINRequired]"
                                outlined
                                dense
                                :maxlength="10"
                              ></v-text-field>
                            </v-col>
                          </v-row>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="12" sm="6">
                          <TimeSheetTypesDropdown
                            label="Time Sheet Type"
                            :value="timeSheetType"
                            :isRequired="true"
                            :readonly="isReadOnlyAccess"
                            :dense="true"
                            @typeChange="(newValue) => this.timeSheetType = newValue"
                          ></TimeSheetTypesDropdown>
                        </v-col>
                        <v-col cols="12" sm="6">
                          <v-text-field
                            v-if="this.timeSheetType === 3"
                            label="Clock In/Clock Out IP Address"
                            v-model="clockIPAddress"
                            dense
                            outlined
                          ></v-text-field>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="12" sm="6">
                          <v-text-field
                            label="VMS Assignment Id"
                            v-model="vmsAssignmentId"
                            :readonly="isReadOnlyAccess"
                            :maxlength="100"
                            :hint="VMS_ASSIGNMENT_ID_HINT"
                            persistent-hint
                            outlined
                            dense
                          ></v-text-field>
                        </v-col>
                      </v-row>
                    </v-col>
                    <v-divider vertical></v-divider>
                    <v-col cols="12" md="6">
                      <v-row>
                        <v-col cols="12" sm="6">
                          <v-row>
                            <v-col cols="12" class="px-3">
                              <v-card-title class="px-0 py-0">
                                Pay Details
                              </v-card-title>
                            </v-col>
                          </v-row>
                          <v-row class="mt-0">
                            <v-col cols="12">
                              <FormattedNumberField
                                label="Pay Rate"
                                :value="payRate"
                                prepend-inner-icon="attach_money"
                                :dense="true"
                                :required="true"
                                :readonly="isReadOnlyAccess"
                                @valueChange="(newValue) => this.payRate = newValue"
                                @blur="setPayRateType"
                              ></FormattedNumberField>
                            </v-col>
                          </v-row>
                          <v-row>
                            <v-col cols="12">
                              <PayRateTypesDropdownList
                                :isRequired="true"
                                :payRateType="payRateTypeId"
                                :readonly="isReadOnlyAccess"
                                :dense="true"
                                @payRateTypeChangeHandler="payRateTypeChangeHandler"
                              >
                              </PayRateTypesDropdownList>
                            </v-col>
                          </v-row>
                          <v-row>
                            <v-col cols="12">
                              <FormattedNumberField
                                label="Monthly Total Pay Rate"
                                :value="totalPayRate"
                                :readonly="true"
                                :hint="totalPayRateHint"
                                :persistent-hint="!!totalPayRateHint"
                                :dense="true"
                                prepend-inner-icon="attach_money"
                              ></FormattedNumberField>
                            </v-col>
                          </v-row>
                        </v-col>
                        <v-divider vertical></v-divider>
                        <v-col cols="12" sm="6">
                          <v-row>
                            <v-col cols="12" class="px-3">
                              <v-card-title class="px-0 py-0">
                                Bill Details
                              </v-card-title>
                            </v-col>
                          </v-row>
                          <v-row class="mt-0">
                            <v-col cols="12">
                              <FormattedNumberField
                                label="Bill Rate"
                                :value="billRate"
                                :required="true"
                                :readonly="isReadOnlyAccess"
                                prepend-inner-icon="attach_money"
                                :dense="true"
                                @valueChange="(newValue) => this.billRate = newValue"
                                @blur="setBillRateType"
                              ></FormattedNumberField>
                            </v-col>
                          </v-row>
                          <v-row>
                            <v-col cols="12">
                              <BillRateTypesDropdownList
                                :isRequired="true"
                                :billRateType="billRateTypeId"
                                :readonly="isReadOnlyAccess"
                                :dense="true"
                                @billRateTypeChangeHandler="(newValue) => this.billRateTypeId = newValue">
                              </BillRateTypesDropdownList>
                            </v-col>
                          </v-row>
                          <v-row>
                            <v-col cols="12">
                              <FormattedNumberField
                                label="Monthly Net Bill Rate"
                                :value="netBillRate"
                                :readonly="true"
                                :hint="netBillRateHint"
                                :persistent-hint="!!netBillRateHint"
                                :dense="true"
                                prepend-inner-icon="attach_money"
                              ></FormattedNumberField>
                            </v-col>
                          </v-row>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="6" class="pb-0">
                          <FormattedNumberField
                            label="Markup %"
                            :value="markupPercentage"
                            :readonly="true"
                            :dense="true"
                            appendIcon="percent"
                            :hideDetails="true"
                          ></FormattedNumberField>
                        </v-col>
                        <v-col cols="6" class="pb-0">
                          <FormattedNumberField
                            label="Monthly Gross Profit"
                            :value="grossProfit"
                            :readonly="true"
                            :hint="grossProfitHint"
                            :persistent-hint="!!grossProfitHint"
                            :dense="true"
                            prepend-inner-icon="attach_money"
                          ></FormattedNumberField>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col
                          class="pt-0"
                          cols="12" sm="4"
                        >
                          <v-checkbox
                            v-if="isHourlyPaid"
                            label="Calculate Automatic OT"
                            v-model="overtimeEligible"
                            :readonly="isReadOnlyAccess"
                            dense
                          ></v-checkbox>
                        </v-col>
                        <v-col cols="12" sm="8">
                          <FormattedNumberField
                            v-if="overtimeEligible"
                            label="Week Threshold (Hours)"
                            :value="overtimeThreshold"
                            :dense="true"
                            :required="overtimeEligible"
                            :readonly="isReadOnlyAccess"
                            @valueChange="(newValue) => this.overtimeThreshold = newValue"
                          ></FormattedNumberField>
                        </v-col>
                      </v-row>
                      <v-row
                        v-for="row in premiumRateRows"
                        :key="row.timeTypeId"
                      >
                        <v-col cols="12" sm="6">
                          <FormattedNumberField
                            :label="`${row.code} Pay Rate`"
                            :value="row.payRate"
                            :dense="true"
                            :required="row.required"
                            :readonly="isReadOnlyAccess"
                            prepend-inner-icon="attach_money"
                            @valueChange="(newValue) => setTimeTypeRate(row.timeTypeId, { payRate: newValue })"
                          ></FormattedNumberField>
                        </v-col>
                        <v-col cols="12" sm="6">
                          <FormattedNumberField
                            :label="`${row.code} Bill Rate`"
                            :value="row.billRate"
                            :dense="true"
                            :required="row.required"
                            :readonly="isReadOnlyAccess"
                            prepend-inner-icon="attach_money"
                            @valueChange="(newValue) => setTimeTypeRate(row.timeTypeId, { billRate: newValue })"
                          ></FormattedNumberField>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="12" sm="6">
                          <v-checkbox
                            label="Contractor Commission Enabled"
                            v-model="contractorCommissionEnabled"
                            :readonly="isReadOnlyAccess || !canSetContractorCommission"
                            dense
                          ></v-checkbox>
                        </v-col>
                        <v-col cols="12" sm="6">
                          <FormattedNumberField
                            v-if="contractorCommissionEnabled"
                            label="Markup %"
                            appendIcon="percent"
                            :value="contractorCommissionMarkup"
                            :readonly="isReadOnlyAccess || !canSetContractorCommission"
                            :dense="true"
                            @valueChange="(newValue) => this.contractorCommissionMarkup = newValue"
                          ></FormattedNumberField>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="12">
                          <v-btn
                            block
                            outlined
                            color="primary"
                            @click="openMarkupCalculator"
                          >
                            Markup Calc.
                          </v-btn>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-tab-item>
                <v-tab-item :key="COMMISSIONS">
                  <v-row class="my-5">
                    <v-col cols="12">
                      <CommissionsCollector
                        ref="commissionCollector"
                        commissionComponent="CommissionInput"
                        commissionType="commission"
                        :commissions="commissions"
                        :readonly="isReadOnlyAccess"
                        @commissionsChange="handleCommissionsChange"
                      ></CommissionsCollector>
                    </v-col>
                  </v-row>
                </v-tab-item>
                <v-tab-item :key="EXPENSES">
                  <v-row class="my-5">
                    <v-col cols="12">
                      <ExpensesCard
                        cardTitle="Expenses"
                        :expensesList="expenses"
                        :readonly="isReadOnlyAccess"
                        :showActive="false"
                        :showExpenseAmountType="true"
                        @expensesListChange="(newValue) => this.expenses = newValue"
                      ></ExpensesCard>
                    </v-col>
                  </v-row>
                </v-tab-item>
              </v-tabs-items>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row justify="end" v-if="isReadOnlyAccess">
        <v-col cols="6" md="3" class="pl-2">
          <v-btn
            block
            outlined
            depressed
            @click="cancel"
          >
            Back
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
    <ButtonsSubmitForm
      v-if="!isReadOnlyAccess"
      :isFormValid="true"
      @save="handleSaveAssignment"
      @cancel="cancel"
      >
    </ButtonsSubmitForm>
    <MarkupCalculator
      ref="markupCalculator"
    ></MarkupCalculator>
    <MessageBox
      ref="messageBox"
      message="There are required fields missing"
      color="error"
      closeTextColor="white"
    ></MessageBox>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import { placementsRoute } from '@/lib/constants/routes'
import { UsersDropDownList } from '@/components/users'
import { CommissionsCollector } from '@/components/commissions'
import { SimpleDatePicker } from '@/components/shared/datePickers'
import { SimplePageHeader } from '@/components/pageComponents'
import { ClientsDropDownList } from '@/components/clients'
import { ContractTypesDropDownList } from '@/components/contractTypes'
import { PayFrequenciesDropdownList } from '@/components/payFrequencies'
import Required from '@/lib/formValidations/required'
import { ButtonsSubmitForm } from '@/components/buttons'
import { getTotalCommisions } from '@/lib/commissions'
import {
  CONTRACT_TO_HIRE,
  LENGTH_OF_CONTRACT_DEFAULT,
  CONVERSION_FEE_DEFAULT
} from '@/lib/constants/contractTypeDefault'
import { ExpensesCard } from '@/components/expenses'
import {
  EMPLOYEE_WITH_BENEFITS,
  EMPLOYEE_NO_BENEFITS,
  CORP_TO_CORP,
  INDEPENDENT_CONTRACTOR
} from '@/lib/constants/employeeTypes'
import { HOURLY, MONTHLY, ANNUAL } from '@/lib/constants/rateTypes'
import { buildPremiumRateRows, withTimeTypeRate } from './assignmentTimeTypeRateHelpers'
import { ADMIN, MANAGER, SALES_PERSON } from '@/lib/constants/securityRoles'
import { getCurrentUser, userHasRoles } from '@/lib/authentication'
import { FormattedNumberField } from '@/components/shared/textFields'
import { MessageBox } from '@/components/shared/messages'
import { TimeSheetTypesDropdown } from '@/components/shared/dropdowns'
import MarkupCalculator from './MarkupCalculator.vue'
import { BillRateTypesDropdownList } from '@/components/billRateTypes'
import { PayRateTypesDropdownList } from '@/components/payRateTypes'
import {
  getOverheadPercent,
  getMonthlyTotalPayRate,
  getMonthlyNetBillRate,
  getMarkup,
  getExpensesDeduction
} from '@/lib/grossProfitCalculator'
import { formatCurrency, formatPercent, getEmployeeTypeLabel } from '@/lib/display'

const VMS_ASSIGNMENT_ID_HINT = "The client's VMS id for this placement. Nightly hours import against it, so copy it onto the new contract when a rate change ends this one."

export default {
  components: {
    UsersDropDownList,
    CommissionsCollector,
    SimpleDatePicker,
    ButtonsSubmitForm,
    SimplePageHeader,
    ContractTypesDropDownList,
    PayFrequenciesDropdownList,
    ClientsDropDownList,
    ExpensesCard,
    FormattedNumberField,
    MessageBox,
    MarkupCalculator,
    PayRateTypesDropdownList,
    BillRateTypesDropdownList,
    TimeSheetTypesDropdown
  },
  data: () => ({
    HOURLY,
    MONTHLY,
    ANNUAL,
    isFormValid: false,
    EMPLOYEE_WITH_BENEFITS,
    EMPLOYEE_NO_BENEFITS,
    CORP_TO_CORP,
    INDEPENDENT_CONTRACTOR,
    tab: null,
    BASIC_INFORMATION: 0,
    CONTRACT_DETAILS: 1,
    COMMISSIONS: 2,
    EXPENSES: 3,
    VMS_ASSIGNMENT_ID_HINT,
    monthlyExpensesDeduction: 0
  }),
  computed: {
    ...mapFields('settings', ['payRateSettings']),
    ...mapFields('timeTypes', ['timeTypesList', 'meta']),
    ...mapFields('assignment', [
      'userId',
      'billRate',
      'startAt',
      'endAt',
      'contractTypeId',
      'lengthOfContract',
      'conversionFee',
      'employeeTypeId',
      'corporateName',
      'active',
      'clientId',
      'overtimeEligible',
      'billRateTypeId',
      'payFrequencyId',
      'payRateTypeId',
      'payRate',
      'ein',
      'commissions',
      'expenses',
      'timeTypeRates',
      'defaultExpensesByClient',
      'vmsFee',
      'workedHoursAverage',
      'jobTitle',
      'timeSheetType',
      'clockIPAddress',
      'overtimeThreshold',
      'overtimeRate',
      'contractorCommissionEnabled',
      'contractorCommissionMarkup',
      'vmsAssignmentId'
    ]),
    id: function () {
      return this.$route.params.id
    },
    premiumRateRows () {
      return buildPremiumRateRows({
        timeTypesList: this.timeTypesList,
        timeTypeRates: this.timeTypeRates,
        meta: this.meta,
        overtimeEligible: this.overtimeEligible
      })
    },
    isContractToHire () {
      return this.contractTypeId === CONTRACT_TO_HIRE
    },
    isC2C () {
      return this.employeeTypeId === CORP_TO_CORP
    },
    isEdit: {
      get () {
        return !!this.id
      }
    },
    isReadOnlyAccess () {
      return !this.userHasRoles({ user: this.getCurrentUser(), allowedRoles: [ADMIN, MANAGER] })
    },
    canSetContractorCommission () {
      return this.userHasRoles({ user: this.getCurrentUser(), allowedRoles: [ADMIN, MANAGER, SALES_PERSON] })
    },
    isHourlyPaid () {
      return this.payRateTypeId === HOURLY
    },
    totalPayRate () {
      if (this.payRate !== null && this.payRateTypeId && this.employeeTypeId && this.workedHoursAverage !== null) {
        return getMonthlyTotalPayRate(getOverheadPercent(this.employeeTypeId, this.payRateSettings), this.payRate, this.payRateTypeId, this.workedHoursAverage)
      }
      return null
    },
    netBillRate () {
      if (this.vmsFee !== null && this.billRate !== null && this.billRateTypeId) {
        return getMonthlyNetBillRate(this.vmsFee, this.billRate, this.billRateTypeId, this.workedHoursAverage)
      }
      return null
    },
    grossProfit () {
      if (this.netBillRate && this.totalPayRate) {
        return this.netBillRate - this.totalPayRate - this.monthlyExpensesDeduction
      }
      return null
    },
    totalPayRateHint () {
      if (this.totalPayRate) {
        return `Total Pay Rate = Pay Rate + ${getEmployeeTypeLabel(this.employeeTypeId)} Overhead (${formatPercent(getOverheadPercent(this.employeeTypeId, this.payRateSettings))})`
      }
      return 'Total Pay Rate = Pay Rate + Overhead'
    },
    netBillRateHint () {
      if (this.netBillRate) {
        return `Net Bill Rate = Bill Rate - VMS Fee (${formatPercent(this.vmsFee)})`
      }
      return 'Net Bill Rate = Bill Rate - VMS Fee'
    },
    grossProfitHint () {
      let hint = 'Gross Profit = Net Bill Rate - Total Pay Rate'
      if (this.monthlyExpensesDeduction > 0) {
        hint += ` - Monthly Expenses (${formatCurrency(this.monthlyExpensesDeduction)})`
      }
      return hint
    },
    markupPercentage () {
      if (this.totalPayRate && this.grossProfit) {
        return getMarkup(this.totalPayRate, this.grossProfit)
      }
      return null
    }
  },
  watch: {
    defaultExpensesByClient () {
      // First delete the default expenses
      if (this.expenses && this.expenses.length > 0) {
        // Look for the client's default expenses
        let defaultExpenses = this.expenses.filter(expense => !!expense.clientDefaultExpense)
        for (let defaultExpense of defaultExpenses) {
          let indexToDelete = this.expenses.findIndex(expense => expense.id === defaultExpense.id && !!expense.clientDefaultExpense)
          this.expenses.splice(indexToDelete, 1)
        }
      }
      // Then include the default expenses of the existing client chosen
      for (let index = this.defaultExpensesByClient.length - 1; index >= 0; index--) {
        this.expenses.unshift({ ...this.defaultExpensesByClient[index], clientDefaultExpense: true })
      }
    },
    expenses () {
      this.monthlyExpensesDeduction = getExpensesDeduction(this.expenses, this.MONTHLY, this.workedHoursAverage)
    }
  },
  methods: {
    userHasRoles,
    getCurrentUser,
    Required,
    getTotalCommisions,
    ...mapActions('assignment', [
      'getAssignment',
      'saveAssignment',
      'clearAssignment'
    ]),
    ...mapActions('settings', ['getSettings']),
    ...mapActions('client', ['getClient']),
    ...mapActions('timeTypes', ['getTimeTypes']),
    loadAssignmentData () {
      this.getAssignment({ id: this.id })
    },
    checkRequiredFields () {
      return this.userId &&
      this.clientId &&
      this.billRate &&
      this.startAt &&
      this.employeeTypeId &&
      this.billRateTypeId &&
      this.payFrequencyId &&
      this.payRateTypeId &&
      this.payRate &&
      this.workedHoursAverage &&
      (this.contractTypeId === CONTRACT_TO_HIRE ? (this.lengthOfContract !== null && this.conversionFee !== null && this.endAt) : true) &&
      (this.employeeTypeId === CORP_TO_CORP ? (this.ein && this.corporateName) : true)
    },
    setTimeTypeRate (timeTypeId, changes) {
      this.timeTypeRates = withTimeTypeRate({ timeTypeRates: this.timeTypeRates, timeTypeId, changes })
    },
    handleSaveAssignment () {
      this.lengthOfContract = this.lengthOfContract || LENGTH_OF_CONTRACT_DEFAULT
      this.conversionFee = this.conversionFee || CONVERSION_FEE_DEFAULT

      if (this.$refs.form.validate() &&
      this.checkRequiredFields() &&
      (this.$refs.commissionCollector ? this.$refs.commissionCollector.validateCommissionsPercentages() : true)
      ) {
        this.saveAssignment()
          .then(() => this.$router.push({ name: placementsRoute.name }))
      } else {
        this.$refs.messageBox.show()
      }
    },
    handleClientIdChange (value) {
      this.clientId = value
      if (this.clientId) {
        this.getClient({ id: this.clientId }).then((client) => {
          this.vmsFee = client.vmsFee
          this.defaultExpensesByClient = client.contractorExpenses.filter(expense => expense.active === true)
        })
      }
    },
    handleUserIdChange (value) {
      this.userId = value
    },
    handleCommissionsChange (value) {
      this.commissions = value
    },
    cancel () {
      this.$router.push({ name: placementsRoute.name })
    },
    isLengthOfContractRequired () {
      return this.isContractToHire ? (this.lengthOfContract === null ? 'Length of Contract is Required' : true) : true
    },
    isConversionFeeRequired () {
      return this.isContractToHire ? (this.conversionFee === null ? 'Conversion Fee is Required' : true) : true
    },
    isCorporateNameRequired () {
      return this.isC2C ? (!this.corporateName ? 'Corporate Name is Required' : true) : true
    },
    isEINRequired () {
      return this.isC2C ? (!this.ein ? 'EIN is Required' : true) : true
    },
    isFilledOut () {
      return this.employeeTypeId ? true : 'Please choose the consultant type'
    },
    endDateChangeHandler (endDate) {
      this.endAt = endDate
      if (!endDate) {
        this.active = true
        return
      }
      let tempEndDate = endDate.split('-')
      let today = new Date().setHours(0, 0, 0, 0)
      if (new Date(tempEndDate[0], parseInt(tempEndDate[1]) - 1, tempEndDate[2]) <= today) {
        this.active = false
      }
    },
    setPayRateType () {
      if (this.payRate && !this.payRateTypeId) {
        if (this.payRate < 500) {
          this.payRateTypeId = HOURLY
        } else if (this.payRate < 19999) {
          this.payRateTypeId = MONTHLY
        } else if (this.payRate >= 20000) {
          this.payRateTypeId = ANNUAL
        }
      }
    },
    setBillRateType () {
      if (this.billRate && !this.billRateTypeId) {
        if (this.billRate < 500) {
          this.billRateTypeId = HOURLY
        } else if (this.billRate < 19999) {
          this.billRateTypeId = MONTHLY
        } else if (this.billRate >= 20000) {
          this.billRateTypeId = ANNUAL
        }
      }
    },
    payRateTypeChangeHandler (newValue) {
      if (newValue === MONTHLY || newValue === ANNUAL) {
        this.overtimeEligible = false
      }
      this.payRateTypeId = newValue
    },
    openMarkupCalculator () {
      this.$refs.markupCalculator.showModal()
    }
  },
  created () {
    this.getTimeTypes()
    this.getSettings().then(() => {
      this.$refs.form.resetValidation()
      if (this.id) {
        this.loadAssignmentData()
      } else {
        this.clearAssignment()
        this.active = true
        this.workedHoursAverage = this.payRateSettings.workedHoursAverageDefault
        this.overtimeThreshold = this.payRateSettings.overtimeThresholdDefault
        this.overtimeRate = this.payRateSettings.overtimeRateDefault
      }
    })
  }
}
</script>
